<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<!-- eslint-disable line-comment-position -->
<template>
  <div
    ref="slotRef"
    class="rounded-md relative aspect-video w-full overflow-hidden"
  >
    <Teleport to="body" :disabled="!isCinema">
      <div
        :class="
          isCinema
            ? 'fixed inset-0 z-[60] grid place-items-center p-16'
            : 'contents'
        "
      >
        <!-- Pointer-only exit surface; the ✕ is the accessible control. -->
        <button
          v-if="isCinema"
          type="button"
          tabindex="-1"
          aria-hidden="true"
          class="absolute inset-0 bg-[white]/80 backdrop-blur-[40px] backdrop-brightness-[115%] backdrop-saturate-[140%] transition-opacity duration-[var(--raw-slow)] ease-[var(--raw-ease-out-quart)]"
          :style="{ opacity: backdropOn ? 1 : 0 }"
          @click="requestClose"
        />

        <!-- Grid item, sized to the frame — anchors the close button below. -->
        <div :class="isCinema ? 'relative' : 'contents'">
          <div
            ref="frameRef"
            data-frame
            tabindex="-1"
            :class="[
              isCinema
                ? 'relative aspect-video w-[min(90vw,calc(80dvh*16/9))] overflow-hidden rounded-[1.5rem] shadow-[0_28px_80px_-24px_#00000024] outline-none'
                : 'absolute inset-0 h-full w-full overflow-hidden',
              isCinema && !showChrome ? 'cursor-none' : '',
            ]"
            @mouseenter="onFrameHover"
            @mousemove="onFrameHover"
            @mouseleave="onFrameLeave"
          >
            <!-- The single, persistent element: Teleport moves this node
                             rather than recreating it, so playback survives the trip. -->
            <video
              ref="videoRef"
              class="h-full w-full object-cover"
              muted
              :autoplay="loops"
              playsinline
              :preload="loops ? 'metadata' : 'none'"
              :poster="optimizedPoster.src"
              @loadedmetadata="syncDuration"
              @durationchange="syncDuration"
              @timeupdate="onTimeUpdate"
              @play="onPlay"
              @pause="onPause"
              @ended="onEnded"
              @volumechange="onVolumeChange"
              @click="onVideoClick"
            >
              <source
                v-if="sources.webm"
                :src="sources.webm"
                type="video/webm"
              />
              <source :src="sources.mp4" type="video/mp4" />
            </video>

            <!-- Inline affordance: the whole box is the tap target,
                             marked by the branded play glyph. -->
            <button
              v-if="!isCinema"
              type="button"
              class="group absolute inset-0 grid cursor-pointer place-items-center"
              :aria-label="i18n(translations.watchLabel, { title })"
              @click="enter"
            >
              <span
                class="raw-glass rounded-full grid h-48 w-48 place-items-center transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] group-hover:bg-[white]/80"
              >
                <RevIllustration
                  src="/mine-my-phone/icons/play--flat.svg"
                  alt=""
                  aria-hidden="true"
                  :width="16"
                  :height="16"
                />
              </span>
            </button>

            <!-- Cinema controls: one row. Reveal on hover / paused /
                             tap; while hidden they blur + slide down. -->
            <div
              v-if="isCinema"
              data-controls
              :data-visible="showChrome || undefined"
              class="absolute inset-x-0 bottom-0 flex items-center gap-16 bg-[linear-gradient(to_top,#00000050,transparent)] p-16 pt-32 text-[white]"
              @click.stop
            >
              <button
                type="button"
                :class="CONTROL_BUTTON"
                :aria-label="
                  i18n(
                    isPaused ? translations.playLabel : translations.pauseLabel,
                  )
                "
                @click="togglePlay"
              >
                <svg
                  v-if="isPaused"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4.5 3v10l8.5-5z" />
                </svg>
                <svg
                  v-else
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <rect x="4" y="2.5" width="3" height="11" rx="1.5" />
                  <rect x="9" y="2.5" width="3" height="11" rx="1.5" />
                </svg>
              </button>

              <span
                class="raw-body-3 whitespace-nowrap tabular-nums !leading-none"
              >
                {{ formatTime(displayTime)
                }}<span class="text-[#ffffff99]"
                  >&nbsp;/ {{ formatTime(duration) }}</span
                >
              </span>

              <!-- Pointer/touch seeking is manual on the wrapper — the
                                 pointer maps straight across the full track width, so
                                 a click/tap lands exactly where you press (a native
                                 range maps through its thumb inset and snaps near the
                                 edges). The tall transparent wrapper is the finger
                                 hitbox; the native range stays for keyboard/AT only
                                 (pointer-events:none). The visible dot is an overlay
                                 at the true progress, riding the fill seam.
                                 `--progress` sits on the wrapper so both children
                                 inherit it. -->
              <div
                ref="scrubberRef"
                data-scrubber-wrap
                :data-scrubbing="scrubbing || undefined"
                class="relative flex-1"
                :style="{ '--progress': progress }"
                @pointerdown="onScrubPointerDown($event)"
                @pointermove="onScrubPointerMove($event)"
                @pointerup="onScrubPointerUp($event)"
                @pointercancel="onScrubPointerUp($event)"
              >
                <input
                  type="range"
                  data-scrubber
                  class="block w-full"
                  min="0"
                  :max="duration || 0"
                  step="any"
                  :value="displayTime"
                  :aria-label="i18n(translations.seekLabel)"
                  @input="onScrub($event)"
                  @change="endScrub"
                />
                <span data-thumb aria-hidden="true" />
              </div>

              <button
                type="button"
                :class="CONTROL_BUTTON"
                :aria-label="
                  i18n(
                    isMuted ? translations.unmuteLabel : translations.muteLabel,
                  )
                "
                @click="toggleMute"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8.5 3 4.5 6H2v4h2.5l4 3z" />
                  <template v-if="isMuted">
                    <path d="M11 6l4 4M15 6l-4 4" />
                  </template>
                  <template v-else>
                    <path d="M11 6c.6.6 1 1.4 1 2s-.4 1.4-1 2" />
                    <path d="M13 4c1.2 1 2 2.4 2 4s-.8 3-2 4" />
                  </template>
                </svg>
              </button>

              <button
                v-if="canFullscreen"
                type="button"
                :class="CONTROL_BUTTON"
                :aria-label="
                  i18n(
                    isFullscreen
                      ? translations.exitFullscreenLabel
                      : translations.fullscreenLabel,
                  )
                "
                @click="toggleFullscreen"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path
                    v-if="isFullscreen"
                    d="M2 6h4V2M10 2v4h4M14 10h-4v4M6 14v-4H2"
                  />
                  <path v-else d="M6 2H2v4M14 6V2h-4M10 14h4v-4M2 10v4h4" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Close: static (not part of the auto-hiding chrome), below
                         the video and centered. Fades only during the exit. -->
          <button
            v-if="isCinema"
            type="button"
            class="raw-glass rounded-full absolute left-1/2 top-full mt-16 grid h-44 w-44 -translate-x-1/2 cursor-pointer place-items-center text-[--raw-verydarkgrey] transition-opacity duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)]"
            :class="closing ? 'pointer-events-none opacity-0' : 'opacity-100'"
            :aria-label="i18n(translations.closeLabel)"
            @click.stop="requestClose"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M1 1L11 11M11 1L1 11"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
// Module scope, deliberately: a `<script setup>` block compiles into `setup()`, so a
// `const` declared up there is per component instance, not shared. Anything the players
// must agree on across mounts belongs in this block, which runs once for the whole app.
// eslint-disable-next-line import/order, import/no-duplicates
import { reactive } from 'vue'
// eslint-disable-next-line import/order
import { useIllustrationOptimizer } from '@ds/plugin'
// `?video=` names an id, not an instance, and the same id is mounted more than once
// (`MetalView` in panel + reader, the gallery listing the same subjects the panels do).
// Newest claim wins; see the claims registry in `.claude/rules/video.md`.
const claims = reactive(new Map<string, symbol[]>())

/** Claims the id for this instance. Mount claims once; a click re-claims, because mount
 *  order only guesses which of the shared-id players the user actually pressed. */
function claimCinema(id: string, token: symbol) {
  const held = claims.get(id) ?? []
  if (held.at(-1) === token) return
  claims.set(id, [...held.filter((one) => one !== token), token])
}

/** Drops this instance's claim; `true` when another instance still holds the id. */
function releaseCinema(id: string, token: symbol): boolean {
  const rest = (claims.get(id) ?? []).filter((held) => held !== token)
  if (rest.length) claims.set(id, rest)
  else claims.delete(id)

  return rest.length > 0
}

// Videos the user has already opened in cinema this session. The first open of a
// given id starts from the top; every later open keeps the live decorative playhead
// instead of rewinding. In-memory only — survives ContentSwap remounts (keyed by
// id), resets on reload. Deliberately not persisted.
const openedInCinema = new Set<string>()
</script>

<script setup lang="ts">
// eslint-disable-next-line import/no-duplicates
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { noop } from '@backmarket/utils/function/noop'
import { RevIllustration } from '@ds/components/Illustration'
import { onKeyStroke, useIntersectionObserver } from '@vueuse/core'

import { useCinemaFlip } from '~/scopes/campaign/composables/useCinemaFlip'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import type { VideoSources } from '~/scopes/campaign/data/videos'

import translations from './NativeVideo.translations'

// How long the controls linger after they're revealed over a playing video.
const CONTROLS_HIDE_MS = 2600
// Shared by the three discrete controls: play, mute, fullscreen.
const CONTROL_BUTTON =
  'grid cursor-pointer place-items-center rounded-[8px] bg-[#ffffff26] p-8 transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:bg-[#ffffff46]'

const props = defineProps<{
  /** Cinema id — the `?video=` param value. Unique among mounted videos. */
  id: string
  /** Self-hosted sources: `webm` preferred where supported, `mp4` fallback. */
  sources: VideoSources
  /** Localized title, used for the accessible label. */
  title: string
  /** Still for the idle box; the browser holds it until the first frame decodes, so it
   *  also covers the grow into cinema. Required with `inline: 'poster'`. */
  poster?: string
  /** Outside cinema: `'loop'` is the muted decorative loop, `'poster'` holds the box
   *  idle with nothing fetched — for lists where many players share the screen. */
  inline?: 'loop' | 'poster'
}>()

const emit = defineEmits<{ play: [] }>()

const i18n = useI18n()

const optimizedPoster = useIllustrationOptimizer(() => props.poster || '')

const { activeCinema, openCinema, closeCinema } = useModalState()

const slotRef = ref<HTMLElement | null>(null) // inline placeholder — reserves layout
const frameRef = ref<HTMLElement | null>(null) // the FLIP frame (video wrapper)
const videoRef = ref<HTMLVideoElement | null>(null)
const scrubberRef = ref<HTMLElement | null>(null) // scrubber hit-strip (pointer seek)

const flip = useCinemaFlip(frameRef)

// This instance's claim on `props.id`, held for as long as it is mounted. Taken in
// `onMounted`, so only ever on the client — the server would never release it.
const token = Symbol('cinema')

// In cinema when the URL param names this id *and* this is the instance that owns it —
// the same id can be on screen twice (see the claims registry above).
const isCinema = computed(
  () =>
    activeCinema.value === props.id && claims.get(props.id)?.at(-1) === token,
)

const loops = computed(() => props.inline !== 'poster')

const closing = ref(false) // guards the exit animation against re-entry

const isMuted = ref(true)
const isPaused = ref(false)
const currentTime = ref(0)
const duration = ref(0)
// While dragging the scrubber the video pauses and seeks live through the frames
// (so you preview exactly where you'll land), then resumes on release. `scrubValue`
// drives the thumb + time so the UI tracks the pointer with no seek lag.
const scrubbing = ref(false)
const scrubValue = ref(0)
const backdropOn = ref(false)
const controlsVisible = ref(false)
const isFullscreen = ref(false)
// Feature-detected on mount; the button hides itself where there is none.
const canFullscreen = ref(false)

// Controls show while explicitly revealed, and always while paused or scrubbing
// (so grabbing the bar can never let them auto-hide out from under you).
const showChrome = computed(
  () => controlsVisible.value || isPaused.value || scrubbing.value,
)

// Element focused before entering cinema, restored on exit.
const opener = ref<HTMLElement | null>(null)

// Pointer-capable device → hover-driven controls; else tap-driven. Read on mount.
let hasHover = false
// Restart from 0 on the next exit (set when the video ends organically).
let restartOnExit = false
let hideTimer: ReturnType<typeof setTimeout> | null = null
// Whether playback should resume once the current scrub is released.
let resumeOnRelease = false

// The position the UI reflects: the pending scrub target while dragging, else
// the live playhead.
const displayTime = computed(() =>
  scrubbing.value ? scrubValue.value : currentTime.value,
)
// A 0–1 fraction (not a percentage): the CSS turns it into both the fill boundary
// and the overlay thumb's position — each `calc(var(--progress) * 100%)`, edge to
// edge — so the dot rides the played/remaining seam at every point. See the CSS.
const progress = computed(() =>
  duration.value ? displayTime.value / duration.value : 0,
)

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return '0:00'
  const total = Math.floor(seconds)
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

// --- Controls visibility -------------------------------------------------------

function revealControls() {
  controlsVisible.value = true
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = null
  // Only a playing video auto-hides; a paused one keeps its controls up.
  if (!isPaused.value) {
    hideTimer = setTimeout(() => {
      controlsVisible.value = false
      hideTimer = null
    }, CONTROLS_HIDE_MS)
  }
}

function hideControls() {
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = null
  controlsVisible.value = false
}

function onFrameHover() {
  if (hasHover && isCinema.value) revealControls()
}
function onFrameLeave() {
  if (hasHover && isCinema.value) hideControls()
}

// --- Playback ------------------------------------------------------------------

// Cinema owns playback directly, and an idle box plays nothing until it is clicked, so
// both bow out here. `inView` is kept because waking a box has to know if it is on screen.
const inView = ref(false)

useIntersectionObserver(
  slotRef,
  (entries) => {
    inView.value = entries.some((entry) => entry.isIntersecting)
    if (isCinema.value || !loops.value) return
    const video = videoRef.value
    if (!video) return
    if (inView.value) {
      void video.play().catch(noop)
    } else {
      video.pause()
    }
  },
  { threshold: 0.25 },
)

/** Back to the still: `load()` (not a rewind) is what repaints the `poster`, and it lands
 *  the playhead on 0 so the next cinema open starts from the top. */
function goIdle() {
  const video = videoRef.value
  if (!video) return
  video.pause()
  video.load()
}

// `flush: 'post'` so `preload` is already `none` when `load()` runs — on `pre` it would
// fire the very metadata fetch going idle exists to avoid.
watch(
  loops,
  (now) => {
    if (isCinema.value) return
    if (!now) goIdle()
    else if (inView.value) void videoRef.value?.play().catch(noop)
  },
  { flush: 'post' },
)

// Safari fires `loadedmetadata` before the duration is known (reporting NaN /
// Infinity), then a `durationchange` once it is — so we listen to both and only
// trust a finite value. Without this the scrubber's max stays 0 and can't move.
function syncDuration() {
  const value = videoRef.value?.duration
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    duration.value = value
  }
}
function onTimeUpdate() {
  if (videoRef.value) currentTime.value = videoRef.value.currentTime
}
function onPlay() {
  isPaused.value = false
  revealControls()
}
function onPause() {
  isPaused.value = true
  revealControls()
}
function onVolumeChange() {
  if (videoRef.value) isMuted.value = videoRef.value.muted
}
// Loop is manual so the video can end organically: in the background it just
// restarts; in cinema an organic end closes back to the decorative state.
function onEnded() {
  const video = videoRef.value
  if (!video) return
  if (isCinema.value) {
    restartOnExit = true
    requestClose()
  } else {
    video.currentTime = 0
    void video.play().catch(noop)
  }
}

function togglePlay() {
  const video = videoRef.value
  if (!video) return
  if (video.paused) void video.play().catch(noop)
  else video.pause()
}

function toggleMute() {
  if (videoRef.value) videoRef.value.muted = !videoRef.value.muted
}

// --- Fullscreen ----------------------------------------------------------------

// Safari's video-only fullscreen, the iPhone's only route in. Not in lib.dom.
type NativeFullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
  webkitExitFullscreen?: () => void
}
function osPlayer(): NativeFullscreenVideo | null {
  return videoRef.value
}

let closeOnFullscreenExit = false

// The frame goes up, not the <video>, so our own chrome goes with it — except on
// iPhone, which has no element fullscreen and gets the OS player instead.
function enterFullscreen() {
  const frame = frameRef.value
  if (frame && document.fullscreenEnabled)
    void frame.requestFullscreen().catch(noop)
  else osPlayer()?.webkitEnterFullscreen?.()
}

function exitFullscreen() {
  if (document.fullscreenElement) void document.exitFullscreen().catch(noop)
  else osPlayer()?.webkitExitFullscreen?.()
}

function toggleFullscreen() {
  if (isFullscreen.value) exitFullscreen()
  else enterFullscreen()
  revealControls()
}

function onFullscreenChange() {
  isFullscreen.value = document.fullscreenElement === frameRef.value
}
// The OS player reports on the element, never through the document.
function onOsPlayerChange(event: Event) {
  isFullscreen.value = event.type === 'webkitbeginfullscreen'
}

// Grabbing the bar pauses playback and scrubs through the frames — the video
// seeks live to the thumb (paused, so you preview the frame) and resumes on
// release, but only if it was playing when the scrub began. A video paused before
// the scrub stays paused at the chosen point.
function startScrub() {
  const video = videoRef.value
  if (!video || scrubbing.value) return
  scrubbing.value = true
  resumeOnRelease = !video.paused
  video.pause() // `showChrome` keeps the controls up for the whole drag
}
// Reflect a seek target in the UI and preview it live on the (paused) video —
// shared by the keyboard range and the pointer handler.
function seekTo(value: number) {
  scrubValue.value = value
  if (videoRef.value) videoRef.value.currentTime = value
}
function onScrub(event: Event) {
  if (!scrubbing.value) startScrub() // keyboard: no pointerdown precedes input
  seekTo(Number((event.target as HTMLInputElement).value))
}
function endScrub() {
  if (!scrubbing.value) return
  currentTime.value = scrubValue.value // keep the thumb where it was dropped
  scrubbing.value = false
  if (resumeOnRelease) void videoRef.value?.play().catch(noop)
  resumeOnRelease = false
}

// Map the pointer straight across the full track width, so a click/tap lands exactly
// where you press (a native range maps through its thumb inset, snapping near the
// edges). The wrapper spans the bar, so its rect is the track.
function seekToClientX(clientX: number) {
  const el = scrubberRef.value
  if (!el || !duration.value) return
  const rect = el.getBoundingClientRect()
  const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
  seekTo(fraction * duration.value)
}
// The tall transparent wrapper is the finger hitbox; pointer capture keeps a drag
// alive past the bar; `touch-action: none` (CSS) stops the page scrolling mid-drag.
// The native range stays for keyboard/AT only.
function onScrubPointerDown(event: PointerEvent) {
  if (!duration.value) return
  startScrub()
  scrubberRef.value?.setPointerCapture(event.pointerId)
  seekToClientX(event.clientX)
}
function onScrubPointerMove(event: PointerEvent) {
  if (scrubbing.value) seekToClientX(event.clientX)
}
function onScrubPointerUp(event: PointerEvent) {
  if (!scrubbing.value) return
  scrubberRef.value?.releasePointerCapture(event.pointerId)
  endScrub()
}

// A click toggles playback on pointer devices; on touch only a paused video plays on
// tap (a playing one just toggles the chrome — pausing takes an explicit ⏸ tap).
function onVideoClick() {
  if (!isCinema.value) return
  if (hasHover || isPaused.value) {
    togglePlay()
    revealControls()
  } else if (controlsVisible.value) {
    hideControls()
  } else {
    revealControls()
  }
}

// --- Enter / exit choreography -------------------------------------------------

function enter() {
  if (isCinema.value) return
  flip.record() // measure the inline rect before the layout flips
  // Safe before `openCinema`: `activeCinema` is not this id yet, so `isCinema` is false
  // everywhere and the re-order wakes no watcher.
  claimCinema(props.id, token)
  emit('play')
  openCinema(props.id)
}

function enterCinema() {
  closing.value = false
  opener.value = (document?.activeElement as HTMLElement | null) ?? null
  const video = videoRef.value
  if (video) {
    // First cinema open of this id plays from the top; every later open keeps
    // the current decorative position instead of rewinding.
    if (!openedInCinema.has(props.id)) video.currentTime = 0
    openedInCinema.add(props.id)
    video.muted = false
    void video.play().catch(noop)
  }
  // Wait for the cinema layout, then FLIP-grow from the recorded inline rect.
  void nextTick(() => {
    requestAnimationFrame(() => {
      backdropOn.value = true
      flip.grow()
      frameRef.value?.focus()
      revealControls()
    })
  })
}

function requestClose() {
  if (closing.value || !isCinema.value) return
  // The FLIP shrink can't measure a frame it doesn't own; the watcher below resumes.
  if (isFullscreen.value) {
    closeOnFullscreenExit = true
    exitFullscreen()
    return
  }
  closing.value = true
  backdropOn.value = false
  hideControls()
  const target = slotRef.value?.getBoundingClientRect() ?? null
  // Shrink onto the inline placeholder, then drop the param once it lands.
  flip.shrink(target, () => closeCinema())
}

function exitedCinema() {
  closing.value = false
  // Runs from a pre-flush watcher; wait for the Teleport to relocate the frame
  // inline before clearing the leftover transform (so nothing flashes).
  void nextTick(() => {
    flip.clear()
    const video = videoRef.value
    if (video) {
      video.muted = true
      if (restartOnExit) video.currentTime = 0
      // The gallery wakes an entry before opening it, so this is the mode holding
      // its meaning on every exit path rather than a path taken today.
      if (!loops.value) goIdle()
      else if (video.paused) void video.play().catch(noop)
    }
    restartOnExit = false
    const el = opener.value
    if (el?.isConnected) el.focus()
    opener.value = null
  })
}

// Resumes the close `requestClose` deferred — the organic `ended` while full-screen.
watch(isFullscreen, (now) => {
  if (now || !closeOnFullscreenExit) return
  closeOnFullscreenExit = false
  requestClose()
})

// This is also the cold deep-link path, not just user entry: the claim is taken in
// `onMounted`, so `isCinema` is false through SSR and the first client render and flips
// once the claim lands. Don't add an `if (isCinema) enterCinema()` back into `onMounted`
// — it would run twice.
watch(isCinema, (now, was) => {
  if (now && !was) enterCinema()
  else if (!now && was) exitedCinema()
})

onKeyStroke('Escape', (event) => {
  if (!isCinema.value) return
  event.preventDefault()
  // Fullscreen first: back to cinema, and a second press closes.
  if (isFullscreen.value) exitFullscreen()
  else requestClose()
})
onKeyStroke(' ', (event) => {
  if (!isCinema.value) return
  // Let a focused control handle Space natively — avoids a double toggle.
  const el = document.activeElement
  if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) return
  event.preventDefault()
  togglePlay()
})
onKeyStroke(['m', 'M'], (event) => {
  if (!isCinema.value) return
  event.preventDefault()
  toggleMute()
  revealControls()
})
onKeyStroke(['f', 'F'], (event) => {
  if (!isCinema.value) return
  event.preventDefault()
  toggleFullscreen()
})

// A locale switch rewrites `sources` in place, and changing `src` does not reload a
// <video> — it needs `load()`. Reachable in production: the locale is a route segment and
// `CampaignHome` survives the navigation, so these players stay mounted across it.
watch(
  () => props.sources.mp4,
  () => {
    const video = videoRef.value
    if (!video) return
    video.load() // leaves it paused; an idle box has nothing to resume
    video.muted = !isCinema.value
    if (isCinema.value || (loops.value && inView.value)) {
      void video.play().catch(noop)
    }
  },
)

onMounted(() => {
  claimCinema(props.id, token)
  hasHover =
    typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
  const video = videoRef.value
  if (video) {
    video.muted = true
    isMuted.value = true
    isPaused.value = video.paused
    // A deep-link ships this <video preload="metadata"> in the SSR HTML, so its
    // duration events can fire before hydration attaches our listeners — lost,
    // leaving the scrubber stuck at 0. Read back a duration that already landed
    // (no-op if none has; the now-attached listeners catch the normal case).
    syncDuration()
    video.addEventListener('webkitbeginfullscreen', onOsPlayerChange)
    video.addEventListener('webkitendfullscreen', onOsPlayerChange)
  }
  document.addEventListener('fullscreenchange', onFullscreenChange)
  canFullscreen.value =
    document.fullscreenEnabled || !!osPlayer()?.webkitEnterFullscreen
})

onBeforeUnmount(() => {
  if (hideTimer) clearTimeout(hideTimer)
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  // Read ownership before dropping the claim, then hand the id over: if another
  // instance still holds it (the reader closing over its panel) that one inherits
  // cinema and playback carries on. Only the last one out drops the param.
  const wasCinema = isCinema.value
  const inherited = releaseCinema(props.id, token)
  if (wasCinema && !inherited) closeCinema()
})
</script>

<style scoped>
/* Hidden controls blur + drop away; revealing (hover/pause/tap) clears all three. */
[data-controls] {
  transition:
    opacity var(--raw-out-quart),
    transform var(--raw-out-quart),
    filter var(--raw-out-quart);
}
[data-controls]:not([data-visible]) {
  opacity: 0;
  transform: translateY(12px);
  filter: blur(8px);
  pointer-events: none;
}

/* Fullscreen: drop the cinema box so the video letterboxes natively. */
[data-frame]:fullscreen {
  width: 100%;
  height: 100%;
  aspect-ratio: auto;
  border-radius: 0;
  box-shadow: none;
  background: black;
}
[data-frame]:fullscreen video {
  object-fit: contain;
}

/* The wrapper is the finger hitbox: a tall transparent strip so a finger catches the
   bar anywhere on its vertical extent, without a giant thumb. Pointer seeking is
   handled manually on it (see the script), mapping the pointer straight across the
   full width — so a click/tap lands exactly where you press. `touch-action: none`
   stops the page scrolling while you drag on touch. */
[data-scrubber-wrap] {
  display: flex;
  align-items: center;
  padding-block: 12px;
  touch-action: none;
  cursor: grab;
}
[data-scrubber-wrap][data-scrubbing] {
  cursor: grabbing;
}

/* The played fill runs edge to edge — `--progress` (0–1) maps straight across the
   full width — so 0:00 reads empty and the end reads full, with no stub at either
   cap. The visible dot is the [data-thumb] overlay, positioned on the same
   boundary, so the two stay locked. Pointer goes to the wrapper; the range is
   keyboard/AT only (`pointer-events: none`). */
[data-scrubber] {
  -webkit-appearance: none;
  appearance: none;
  min-width: 0;
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    white calc(var(--progress, 0) * 100%),
    color-mix(in oklab, white 35%, transparent) calc(var(--progress, 0) * 100%)
  );
  pointer-events: none;
}
/* The native thumb is unused — pointer is manual, the visible dot is [data-thumb] —
   so it's zero-sized, which also leaves keyboard mapping with no thumb inset. */
[data-scrubber]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 0;
  height: 0;
}
[data-scrubber]::-moz-range-thumb {
  width: 0;
  height: 0;
  border: 0;
}

/* Overlay dot: centred on the true progress (`left`), reaching both ends and riding
   the fill seam. `pointer-events: none` so drags pass through to the wrapper beneath.
   Visibility: hover devices show it on hover / through the drag (`[data-scrubbing]`);
   touch keeps it up the whole time the chrome does — both ride the [data-controls]
   fade above. */
[data-thumb] {
  position: absolute;
  top: 50%;
  left: calc(var(--progress, 0) * 100%);
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  background: white;
  box-shadow: 0 1px 4px color-mix(in oklab, black 30%, transparent);
  transform: translate(-50%, -50%);
  pointer-events: none;
  opacity: 1;
  transition:
    opacity var(--raw-out-quart),
    scale var(--raw-out-quart);
  transform-origin: top left;
}
@media (hover: hover) {
  [data-thumb] {
    opacity: 0;
    scale: 50%;
  }
  [data-scrubber-wrap]:hover [data-thumb],
  [data-scrubber-wrap][data-scrubbing] [data-thumb] {
    opacity: 1;
    scale: 100%;
  }
}
</style>
